import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { WalletEmailAlertConfigService } from '../services/wallet-email-alert-config.service';
import { EmailValidatorDirective } from 'src/app/shared/directives/email-validator.directive';
import { EmailBlacklistValidatorDirective } from 'src/app/shared/directives/email-blacklist-validator.directive';
import { ButtonComponent } from '../../button/button.component';
import {
  AlertEmail,
  createEmptyAlertEmail,
} from 'src/app/services/models/alert-service/alert-email.model';

export type WalletEmailAlertRegisterDialogProps = {
  alertEmail: AlertEmail;
  allEmails: string[];
  walletId: string;
};

@Component({
  selector: 'app-wallet-email-alert-register-dialog',
  templateUrl: './wallet-email-alert-register-dialog.component.html',
  styleUrls: ['./wallet-email-alert-register-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatSlideToggleModule,
    EmailValidatorDirective,
    EmailBlacklistValidatorDirective,
    ButtonComponent,
  ],
})
export class WalletEmailAlertRegisterDialogComponent {
  alertEmail: AlertEmail = createEmptyAlertEmail();
  addedEmails: string[] = [];
  isEditing = false;
  submited = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data: WalletEmailAlertRegisterDialogProps,
    private readonly dialogRef: MatDialogRef<WalletEmailAlertRegisterDialogComponent>,
    private readonly walletEmailAlertConfigService: WalletEmailAlertConfigService,
  ) {
    dialogRef.updateSize('350px');
    this.alertEmail.walletId = this.data.walletId;
    this.addedEmails = [...this.data.allEmails];

    if (this.data?.alertEmail?.email) {
      this.alertEmail = { ...this.data.alertEmail };

      this.addedEmails = this.addedEmails.filter(
        (email) => email.toLowerCase() !== this.alertEmail.email.toLowerCase(),
      );
      this.isEditing = true;
    }
  }

  handleCancel() {
    this.dialogRef.close();
  }

  onSubmit(form: NgForm) {
    this.submited = true;

    if (!form.valid) return;

    this.isEditing ? this.editEmail() : this.createEmail();
  }

  async createEmail() {
    const email = await this.walletEmailAlertConfigService.createEmail(this.alertEmail);
    email && this.dialogRef.close(email);
  }

  async editEmail() {
    const email = await this.walletEmailAlertConfigService.updateEmail(this.alertEmail);
    email && this.dialogRef.close(email);
  }

  isEmailAlreadyAdded() {
    return this.addedEmails.some((email) => {
      return email.toLowerCase() === this.alertEmail.email.toLowerCase();
    });
  }

  isInvalidEmail(invalid: boolean) {
    return invalid || this.isEmailAlreadyAdded();
  }

  getInvalidEmailMessage(invalid: boolean) {
    if (this.isEmailAlreadyAdded())
      return '* ' + $localize`:@@emailAlreadyAdded:Este e-mail ya ha sido añadido`;
    if (invalid && this.alertEmail.email) return '* E-mail inválido';
    return $localize`:@@mandatoryName:* Campo requerido`;
  }

  get confirmButtonText() {
    return this.isEditing ? $localize`:@@save:Guardar` : $localize`:@@include:Incluir`;
  }

  get title() {
    return this.isEditing
      ? $localize`:@@alertEmailEdition:Edición de e-mail para alerta`
      : $localize`:@@alertEmailInclusion:Inclusión de e-mail para alerta`;
  }

  get hasAnyChange() {
    const { email, position, status } = this.data.alertEmail || {};

    return (
      this.alertEmail.email !== email ||
      this.alertEmail.position !== position ||
      this.alertEmail.status !== status
    );
  }
}
